// shared-types/src/accountExchangeRate/types.ts - Account exchange rate configuration types
import { BaseEntityWithAccount } from '../common'
import { Currency } from '../common/Currency'

export interface AccountExchangeRate extends BaseEntityWithAccount {
    id: string
    accountId: string
    // Moneda base (ej: USD)
    baseCurrency: Currency
    // Moneda objetivo (ej: ARS)
    targetCurrency: Currency
    // Tipo de configuración
    configurationType: AccountExchangeRateType
    // Tasa manual (solo para MANUAL)
    manualRate?: number
    // Ajuste porcentual sobre la tasa global (solo para AUTOMATIC_WITH_ADJUSTMENT)
    adjustmentPercentage?: number
    // Configuración de redondeo (solo para AUTOMATIC_WITH_ADJUSTMENT)
    roundingConfig?: RoundingConfig
    // Indica si está activo
    isActive: boolean
    // Fecha de última actualización manual
    lastManualUpdate?: Date
    // Metadata adicional
    metadata?: AccountExchangeRateMetadata
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date
}

// Tipos de configuración disponibles
export enum AccountExchangeRateType {
    // Usa automáticamente la tasa global más reciente
    AUTOMATIC = 'AUTOMATIC',
    // Usa la tasa global pero permite ajustes porcentuales y redondeo
    AUTOMATIC_WITH_ADJUSTMENT = 'AUTOMATIC_WITH_ADJUSTMENT',
    // Tasa completamente manual
    MANUAL = 'MANUAL',
}

// Configuración de redondeo
export interface RoundingConfig {
    // Método de redondeo
    method: RoundingMethod
    // Precisión decimal (ej: 2 para centavos)
    decimalPlaces: number
    // Regla especial de redondeo (opcional)
    roundingRule?: RoundingRule
}

// Métodos de redondeo disponibles
export enum RoundingMethod {
    // Redondeo estándar (0.5 redondea hacia arriba)
    ROUND = 'ROUND',
    // Siempre redondea hacia arriba
    CEIL = 'CEIL',
    // Siempre redondea hacia abajo
    FLOOR = 'FLOOR',
    // Redondeo bancario (half to even)
    BANKERS = 'BANKERS',
}

// Reglas especiales de redondeo
export enum RoundingRule {
    // Redondea al múltiplo de 5 más cercano (ej: 123.45 -> 123.45, 123.46 -> 123.50)
    ROUND_TO_5_CENTS = 'ROUND_TO_5_CENTS',
    // Redondea al múltiplo de 10 más cercano
    ROUND_TO_10_CENTS = 'ROUND_TO_10_CENTS',
    // Sin regla especial
    NONE = 'NONE',
}

// Metadata adicional
export interface AccountExchangeRateMetadata {
    // Descripción de la configuración
    description?: string
    // Notas adicionales
    notes?: string
    // Información adicional específica del account
    [key: string]: any
}

// DTOs para operaciones
export interface CreateAccountExchangeRateDto {
    baseCurrency: Currency
    targetCurrency: Currency
    configurationType: AccountExchangeRateType
    manualRate?: number
    adjustmentPercentage?: number
    roundingConfig?: RoundingConfig
    metadata?: AccountExchangeRateMetadata
}

export interface UpdateAccountExchangeRateDto {
    configurationType?: AccountExchangeRateType
    manualRate?: number
    adjustmentPercentage?: number
    roundingConfig?: RoundingConfig
    isActive?: boolean
    metadata?: AccountExchangeRateMetadata
}

export interface AccountExchangeRateQueryDto {
    baseCurrency?: Currency
    targetCurrency?: Currency
    configurationType?: AccountExchangeRateType
    isActive?: boolean
    limit?: number
    offset?: number
}

// Tipos para respuestas
export interface AccountExchangeRateResponse {
    item: AccountExchangeRate
}

// DTO para actualizar todo (tasas independientes)
export interface UpdateAccountExchangeRateAllDto {
    globalConfig?: any // Deprecated, kept for compatibility during transition
    rates: Array<{
        id?: string // Si existe, actualizar; si no, crear
        targetCurrency: Currency
        configurationType: AccountExchangeRateType
        manualRate?: number
        adjustmentPercentage?: number
        roundingConfig?: RoundingConfig
    }>
}

// Respuesta del GET con estructura completa
export interface AccountExchangeRateListResponse {
    storeCurrency: Currency // Moneda de la tienda (account.currency)
    paymentCurrencies: Currency[] // Monedas de procesadores detectadas
    rates: AccountExchangeRateWithEffectiveRate[] // Array de tasas con effectiveRate calculado
}

// Extensión de AccountExchangeRate con tasa efectiva
export interface AccountExchangeRateWithEffectiveRate extends AccountExchangeRate {
    effectiveRate: number // Tasa calculada: 1 storeCurrency = X targetCurrency
    baseGlobalRate: number // Tasa base de exchange_rate
    source: string // Fuente de la tasa global
    effectiveDate: Date // Fecha de la tasa global
}

// Tipo para configuración completa de monedas por account
export interface AccountCurrencyConfig {
    accountId: string
    // Moneda principal del account
    primaryCurrency: Currency
    // Todas las configuraciones de tasa para este account
    exchangeRates: AccountExchangeRate[]
    // Tasas efectivas calculadas (incluyendo ajustes)
    effectiveRates: EffectiveExchangeRate[]
}

// Tasa efectiva calculada (lo que realmente se usa para conversiones)
export interface EffectiveExchangeRate {
    baseCurrency: Currency
    targetCurrency: Currency
    // Tasa final después de aplicar ajustes y redondeo
    effectiveRate: number
    // Tasa base global utilizada
    baseGlobalRate: number
    // Tipo de configuración aplicada
    configurationType: AccountExchangeRateType
    // Fecha efectiva de la tasa global
    effectiveDate: Date
    // Fuente de la tasa global
    source: string
}
